/**
 * Sidebar de navegación estilo Notion/Confluence.
 * - Secciones con subsecciones colapsables.
 * - Subsecciones con sus artículos.
 * - Scroll independiente.
 */
import { useState, useMemo } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight, BookOpen, Hash, FileText, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useNavTree } from '@/features/content/queries';
import type { SectionTreeNode } from '@/lib/types';

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: Props) {
  const { data, isLoading } = useNavTree();

  return (
    <aside
      className={cn(
        'bg-card flex h-full flex-col border-r transition-all duration-200',
        collapsed ? 'w-12' : 'w-72',
      )}
    >
      <div className="flex h-14 items-center justify-between border-b px-3">
        {!collapsed && (
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-semibold tracking-tight"
          >
            <span className="bg-brand text-brand-foreground flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold">
              RE
            </span>
            <span className="truncate">Base de Conocimiento</span>
          </Link>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onToggle}
          title={collapsed ? 'Expandir' : 'Colapsar'}
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      </div>

      {!collapsed && (
        <ScrollArea className="flex-1">
          <nav className="px-2 py-3">
            {isLoading ? (
              <SidebarSkeleton />
            ) : (
              (data?.sections ?? []).map((section) => (
                <SectionNode key={section.id} section={section} />
              ))
            )}
          </nav>
        </ScrollArea>
      )}
    </aside>
  );
}

function SidebarSkeleton() {
  return (
    <div className="space-y-3 px-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-6 w-full" />
      ))}
    </div>
  );
}

function SectionNode({ section }: { section: SectionTreeNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const sectionPath = `/seccion/${section.slug}`;
  const isOpenInitial = path.startsWith(sectionPath);
  const [open, setOpen] = useState(isOpenInitial);

  const totalArticles = useMemo(
    () => section.subsections.reduce((acc, s) => acc + (s.articles?.length ?? 0), 0),
    [section.subsections],
  );

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'hover:bg-accent flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm font-medium transition-colors',
        )}
      >
        <ChevronRight
          className={cn(
            'text-muted-foreground h-3.5 w-3.5 transition-transform',
            open && 'rotate-90',
          )}
        />
        <BookOpen className="text-muted-foreground h-4 w-4" />
        <span className="flex-1 truncate">{section.titulo}</span>
        <span className="text-muted-foreground text-[10px]">{totalArticles}</span>
      </button>
      {open && (
        <ul className="ml-3 mt-0.5 border-l pl-2">
          {section.subsections.map((sub) => (
            <SubsectionNode key={sub.id} section={section} sub={sub} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SubsectionNode({
  section,
  sub,
}: {
  section: SectionTreeNode;
  sub: SectionTreeNode['subsections'][number];
}) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const subPath = `/seccion/${section.slug}/${sub.slug}`;
  const isInside = path.startsWith(subPath);
  const [open, setOpen] = useState(isInside);

  return (
    <li>
      <div className="flex items-center">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setOpen((v) => !v);
          }}
          className="text-muted-foreground hover:text-foreground flex h-7 w-5 items-center justify-center"
        >
          <ChevronRight
            className={cn('h-3 w-3 transition-transform', open && 'rotate-90')}
          />
        </button>
        <Link
          to="/seccion/$section/$subsection"
          params={{ section: section.slug, subsection: sub.slug }}
          className={cn(
            'group hover:bg-accent flex flex-1 items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
            path === subPath && 'bg-accent text-foreground font-medium',
          )}
          activeProps={{ className: 'bg-accent font-medium' }}
        >
          <Hash className="text-muted-foreground h-3.5 w-3.5" />
          <span className="truncate">{sub.titulo}</span>
        </Link>
      </div>
      {open && sub.articles && sub.articles.length > 0 && (
        <ul className="ml-6 border-l pl-2">
          {sub.articles.map((art) => (
            <li key={art.id}>
              <Link
                to="/seccion/$section/$subsection/$article"
                params={{ section: section.slug, subsection: sub.slug, article: art.slug }}
                className="text-muted-foreground hover:bg-accent hover:text-foreground flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors"
                activeProps={{ className: 'bg-accent text-foreground font-medium' }}
              >
                <FileText className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{art.titulo}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
